import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["src", "alt"]

import { getSaveImageUrl } from '@/helpers';
import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
} from 'vue';
import {
  useStore,
} from 'vuex';
import ResponsiveSize from '@/classes/ResponsiveSize';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSearchPlus } from '@fortawesome/pro-light-svg-icons';


export default /*#__PURE__*/_defineComponent({
  __name: 'ResponsiveImage',
  props: {
  mediaSizes: {
    type: [String, Object],
    required: false,
  },
  mediaAlt: {
    type: String,
    required: false,
    default: () => null,
  },
  fixedHeight: {
    type: String,
    required: false,
    default: () => '1',
  },
  height: {
    type: String,
    required: false,
  },
  align: {
    type: String,
    required: false,
    default: () => 'center',
  },
  fixedHeightMobile: {
    type: String,
    required: false,
    default: () => '1',
  },
  heightMobile: {
    type: String,
    required: false,
  },
  alignMobile: {
    type: String,
    required: false,
    default: () => 'center',
  },
  enlargeable: {
    type: String,
    required: false,
    default: () => '0',
  },
  isEnlargement: {
    type: Boolean,
    required: false,
    default: () => false,
  },
},
  setup(__props) {

library.add(faSearchPlus);

const props = __props;

const store = useStore();
const root = ref<InstanceType<typeof HTMLElement>>();

const mediaSources = computed<Record<string, Record<string, unknown>>>(() => {
  let ms = props.mediaSizes as string | Record<string, Record<string, unknown>>;

  if (typeof ms === 'string') {
    try {
      ms = JSON.parse(atob(ms || btoa('{}')));
    } finally {
      if (typeof ms === 'string') {
        ms = {};
      }
    }
  }

  return ms || {};
});

const imageSrc = ref<null | string>(null);
const imageHeight = ref<null | string>(null);
const imageWidth = ref<null | string>(null);
const currentImageSrc = ref<null | Record<string, unknown>>(null);

const setImageSource = () => {
  imageSrc.value = null;

  let sizeKeys = ['full'];

  if (!props.isEnlargement) {
    sizeKeys = ['medium', 'large', 'full'];
  }

  sizeKeys.forEach((key) => {
    if (
      root.value
        && mediaSources.value[key]
        && mediaSources.value[key].width
        && parseInt(`${mediaSources.value[key].width}`, 10) > root.value.offsetWidth
    ) {
      currentImageSrc.value = mediaSources.value[key];
      imageSrc.value = getSaveImageUrl(mediaSources.value[key].url) || '';
    }
  });

  if (imageSrc.value === null) {
    const sourceKeys = Object.keys(mediaSources.value);

    if (sourceKeys.length >= 1) {
      currentImageSrc.value = mediaSources.value.full || mediaSources.value[sourceKeys[0]];
      imageSrc.value = getSaveImageUrl(currentImageSrc.value.url) || '';
    }
  }
};

const setImageHeight = () => {
  if (props.isEnlargement) {
    imageWidth.value = `${currentImageSrc.value?.width}px`;
  } else if (ResponsiveSize.getCurrentBreakpoint() === 'lg') {
    if (parseInt(props.fixedHeight, 10) === 1) {
      imageWidth.value = null;
      imageHeight.value = ResponsiveSize.getCSSValue(props.height as string);
    } else {
      imageHeight.value = null;
      imageWidth.value = '100%';
    }
  } else if (parseInt(props.fixedHeightMobile, 10) === 1) {
    imageWidth.value = null;
    imageHeight.value = `${props.heightMobile}px`;
  } else {
    imageHeight.value = null;
    imageWidth.value = '100%';
  }
};

const setEventHandler = () => {
  window.addEventListener('resize', setImageSource);
  window.addEventListener('load', setImageSource);
  setImageSource();
  if (props.height) {
    window.addEventListener('resize', setImageHeight);
    window.addEventListener('load', setImageHeight);
    setImageHeight();
  }
};

const handleIntersect = (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
): void => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      setEventHandler();
      if (root.value) {
        observer.unobserve(root.value);
      }
    }
  });
};

const createObserver = (): void => {
  const observer = new IntersectionObserver(handleIntersect, {
    root: null,
    threshold: 0,
  });

  if (root.value) {
    observer.observe(root.value);
  }
};

onMounted(() => {
  setImageHeight();
  if (window.IntersectionObserver) {
    createObserver();
  } else {
    setEventHandler();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setImageSource);
  window.removeEventListener('load', setImageSource);
  if (props.height) {
    window.removeEventListener('resize', setImageHeight);
    window.removeEventListener('load', setImageHeight);
  }
});

const classes = computed<string[]>(() => {
  const cls = ['io-responsive-image'];

  cls.push(`align-${props.align}`);
  cls.push(`align-mobile-${props.alignMobile}`);

  if (props.enlargeable === '1') {
    cls.push('enlargeable');
  }

  return cls;
});

const enlarge = () => {
  store.commit('enlargement/SET_PROPS', { ...props });
  store.commit('enlargement/SHOW');
};

return (_ctx: any,_cache: any) => {
  const _component_FontAwesomeIcon = _resolveComponent("FontAwesomeIcon")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: _normalizeClass(classes.value)
  }, [
    (imageSrc.value)
      ? (_openBlock(), _createElementBlock("img", {
          key: 0,
          src: imageSrc.value,
          style: _normalizeStyle({
              height: imageHeight.value,
              width: imageWidth.value,
              display: imageWidth.value ? 'block' : 'inline-block',
            }),
          alt: __props.mediaAlt
        }, null, 12, _hoisted_1))
      : _createCommentVNode("", true),
    (__props.enlargeable === '1')
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "enlarge-toggle",
          title: "Klickem um Bild zu vergrößern",
          onClick: enlarge
        }, [
          _createVNode(_component_FontAwesomeIcon, { icon: _unref(faSearchPlus) }, null, 8, ["icon"])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})